import React from 'react';
import styled from 'styled-components';

interface LocationSelectorProps {
  usingCurrentLocation: boolean;
  currentLocationData: any;
  loadingLocation: boolean;
  onLocationTypeChange: (useCurrentLocation: boolean) => void;
  onGetCurrentLocation: () => void;
  /** Label for the non-GPS option; guests have no saved address. */
  addressLabel?: string;
}

const LocationSelector: React.FC<LocationSelectorProps> = ({
  usingCurrentLocation,
  currentLocationData,
  loadingLocation,
  onLocationTypeChange,
  onGetCurrentLocation,
  addressLabel = 'My Address',
}) => {
  return (
    <LocationSection>
      <LocationTitle>Choose Location</LocationTitle>
      <LocationOptions>
        <LocationOption 
          isSelected={!usingCurrentLocation}
          onClick={() => onLocationTypeChange(false)}
        >
          <LocationIcon>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
            </svg>
          </LocationIcon>
          <LocationText>{addressLabel}</LocationText>
        </LocationOption>
        
        <LocationOption 
          isSelected={usingCurrentLocation}
          onClick={onGetCurrentLocation}
          disabled={loadingLocation}
        >
          <LocationIcon>
            {loadingLocation ? (
              <Spinner />
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3A8.994 8.994 0 0 0 13 3.06V1h-2v2.06A8.994 8.994 0 0 0 3.06 11H1v2h2.06A8.994 8.994 0 0 0 11 20.94V23h2v-2.06A8.994 8.994 0 0 0 20.94 13H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"/>
              </svg>
            )}
          </LocationIcon>
          <LocationText>{loadingLocation ? 'Getting location...' : 'Current Location'}</LocationText>
        </LocationOption>
      </LocationOptions>
      
      {/* Show current location info if using it */}
      {usingCurrentLocation && currentLocationData && (
        <CurrentLocationInfo>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="#C9A84C">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
          </svg>
          <span>Showing stylists near {currentLocationData.city}, {currentLocationData.state}</span>
        </CurrentLocationInfo>
      )}
    </LocationSection>
  );
};

// Spinner component
const Spinner = () => (
  <SpinnerContainer>
    <SpinnerCircle />
  </SpinnerContainer>
);

// Styled components
const LocationSection = styled.section`
  background-color: #fff;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const LocationTitle = styled.h3`
  font-size: 18px;
  font-weight: 500;
  color: #171a20;
  margin-bottom: 16px;
`;

const LocationOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const LocationOption = styled.button<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  padding: 12px 20px;
  background-color: ${props => props.isSelected ? '#FAF8F3' : '#f5f5f5'};
  border: 1px solid ${props => props.isSelected ? '#C9A84C' : '#e0e0e0'};
  border-radius: 40px;
  min-width: 180px;
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    background-color: ${props => props.isSelected ? '#FAF8F3' : '#ebebeb'};
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: wait;
  }
`;

const LocationIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  color: ${props => props.color || 'currentColor'};
`;

const LocationText = styled.span`
  font-size: 14px;
  font-weight: 500;
  flex: 1;
`;

const CurrentLocationInfo = styled.div`
  display: flex;
  align-items: center;
  background-color: #FAF8F3;
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 16px;
  color: #A8872A;
  font-size: 14px;
  
  svg {
    margin-right: 8px;
  }
`;

const SpinnerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
`;

const SpinnerCircle = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(0, 122, 255, 0.2);
  border-top-color: rgba(0, 122, 255, 1);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export default LocationSelector;